import { useTwoUITelemetry } from "@amx/ux-twoui-telemetry";
import { IImageProps, Image as FluentImage, ImageLoadState } from "@fluentui/react";
import React, { useEffect,  useRef,  useState } from "react";
import { useComponentLogger } from "../../shared/logging";
import { validateProps } from "../../shared/props";
import { getImageStyles } from "./Image.styles";
import { ImageProps, toFluentImageFit } from "./Image.types";
import { ImageFallback } from "./ImageFallback";

export const Image: React.FC<ImageProps> = ({
    imageFit,
    src,
    errorSrc,
    borderRadius,
    height,
    width,
    grayscale,
    disabled,
    enableHighContrastMode,
    imageGradientType,
    ...otherProps
}) => {
    useComponentLogger("Image", otherProps.id);

    validateProps("Image", otherProps);

    const twoUITelemetry = useTwoUITelemetry();

    const [imgSrc, setImgSrc] = useState(src);
    const [isImgFallback, setIsImgFallback] = useState(false);

    useEffect(() => {
        twoUITelemetry.checkUrlAndReportHttp(src);
        setImgSrc(src);
    }, [src]);

    //useEffect running after setIsImgFallback(true) in handleLoadingStateChange, leads to false assignment of isImageFallback
    //So use boolean to not run this useEffect on first Render
    const initialRender = useRef(true);
    useEffect(() => {
        if(initialRender.current) {
            initialRender.current = false;
        } else {
            setIsImgFallback(false);
        }
    }, [imgSrc]);

    const handleLoadingStateChange: IImageProps["onLoadingStateChange"] = loadState => {
        // When imgSrc is undefined or null, it should render the fallback image instead of nothing.
        if (loadState === ImageLoadState.error || imgSrc === undefined || imgSrc === null) {
            // Allow for fallback to icon when errorSrc also fails. The second time we get an image load
            // state error means that errorSrc url also failed. Fallback to icon at that point.
            if (errorSrc && imgSrc !== errorSrc) {
                setImgSrc(errorSrc);
            } else {
                setIsImgFallback(true);
            }
        }
    };

    let conditionalProps = {};

    // don't need to check "altTextImage" as the alt flows through in `otherProps`
    if (otherProps.imageType === "ariaHiddenImage") {
        conditionalProps = { "aria-hidden": true };
    }

    if (isImgFallback) {
        return (
            <ImageFallback height={height} width={width} {...otherProps} />
        );
    }

    return (
        <FluentImage
            onLoadingStateChange={handleLoadingStateChange}
            imageFit={imageFit && toFluentImageFit[imageFit]}
            src={imgSrc}
            styles={getImageStyles(grayscale, disabled, borderRadius, enableHighContrastMode, imageGradientType)}
            height={height}
            width={width}
            {...conditionalProps}
            {...otherProps}
        />
    );
};
